import type { DayPickerProps } from "react-day-picker";
import { bosnianLongMonths, intlLocaleFor, type AppLocale } from "@/lib/locale";
import { m } from "@/paraglide/messages.js";

type DayPickerI18nProps = Pick<DayPickerProps, "formatters" | "labels" | "lang" | "weekStartsOn">;
type WeekDay = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/**
 * First day of the week per locale (0 = Sunday … 6 = Saturday), matching CLDR
 * `Intl.Locale#getWeekInfo().firstDay` for the `intlLocaleFor` region (unit-tested
 * against Intl where the runtime supports it). A static table instead of a runtime
 * lookup: Firefox has no weekInfo, and the SSR host and browser must agree so the
 * calendar grid never hydrates differently.
 */
export const WEEK_START: Record<AppLocale, WeekDay> = {
  en: 0,
  es: 0,
  de: 1,
  zh: 1,
  ar: 6,
  fr: 1,
  vi: 1,
  sw: 0,
  th: 0,
  bs: 1,
  ja: 0,
  ko: 0,
  pt: 0,
  it: 1,
  sv: 1,
  nl: 1,
  da: 1,
  nb: 1,
  fi: 1,
  pl: 1,
  ru: 1,
  tr: 1,
  hi: 0,
  id: 0,
  ms: 1,
  cs: 1,
  el: 1,
  he: 0,
  af: 0,
  hu: 1,
};

/**
 * Localized react-day-picker caption, weekday headers and screen-reader labels.
 *
 * react-day-picker defaults to English ("Go to the Next Month", "Monday, October 12th,
 * 2026"). Non-en locales get Intl date text plus catalog strings for the nav buttons and
 * the today/selected markers, and the locale's first day of the week. `en` keeps the
 * library defaults (Sunday start) so its accessible names (used by e2e selectors) stay
 * unchanged.
 *
 * Picker cells are local-midnight Dates, so Intl formats them without a `timeZone`
 * option: the host's own zone always reproduces the cell's Y-M-D (UTC on the SSR host,
 * local in the browser), which keeps server and client markup identical.
 */
export function dayPickerI18n(locale: AppLocale): DayPickerI18nProps {
  if (locale === "en") return {};

  const intl = intlLocaleFor(locale);
  const caption = new Intl.DateTimeFormat(intl, { month: "long", year: "numeric" });
  const weekdayShort = new Intl.DateTimeFormat(intl, { weekday: "short" });
  const weekdayLong = new Intl.DateTimeFormat(intl, { weekday: "long" });
  const fullDate = new Intl.DateTimeFormat(intl, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const opts = { locale };
  // bs formats via sr-Latn-BA; fix its Serbian long month names (jun → juni …).
  const months = (text: string) => (locale === "bs" ? bosnianLongMonths(text) : text);
  const captionText = (date: Date) => months(caption.format(date));

  const dayLabel = (date: Date, modifiers?: { today?: boolean; selected?: boolean }) => {
    let label = months(fullDate.format(date));
    if (modifiers?.today) label = m.booking_calendar_day_today({ date: label }, opts);
    if (modifiers?.selected) label = m.booking_calendar_day_selected({ date: label }, opts);
    return label;
  };

  return {
    // bs formats via sr-Latn-BA (see intlLocaleFor) but the markup still declares Bosnian.
    lang: locale === "bs" ? "bs-BA" : intl,
    weekStartsOn: WEEK_START[locale],
    formatters: {
      formatCaption: (month) => captionText(month),
      formatWeekdayName: (weekday) => weekdayShort.format(weekday),
    },
    labels: {
      labelGrid: (month) => captionText(month),
      labelWeekday: (weekday) => weekdayLong.format(weekday),
      labelDayButton: (date, modifiers) => dayLabel(date, modifiers),
      labelGridcell: (date, modifiers) => dayLabel(date, modifiers),
      labelNext: () => m.booking_calendar_next_month({}, opts),
      labelPrevious: () => m.booking_calendar_prev_month({}, opts),
    },
  };
}
